import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image"
import styled, { CSSProp } from "styled-components"

import Button from "components/buttons/Button"
import { TestimonialsContext } from "components/Providers"
import colors from "styles/colors"
import media from "styles/media"
import text from "styles/text"
import { isBrowser } from "utils/functions"

export interface Testimonial {
  kicker?: string
  title: string
  quoteSize?: CSSProp
  logo?: string | IGatsbyImageData | undefined
  person: {
    image: IGatsbyImageData | undefined
    name: string
    title: string
  }
}

interface Props {
  noLogos?: boolean
  items: Testimonial[]
  primaryColor: string
  secondaryColor: string
}

export default function Testimonials({
  noLogos = false,
  items,
  primaryColor,
  secondaryColor,
}: Props) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [wrapper, setWrapper] = useState<HTMLDivElement | null>(null)
  const [logoWrap, setLogoWrap] = useState<HTMLDivElement | null>(null)
  const [wrapperHeight, setWrapperHeight] = useState(0)
  const [logoWrapHeight, setLogoWrapHeight] = useState(0)
  const { activeTestimonialPage, setActiveVideo, setActivePage, activePage } =
    useContext(TestimonialsContext)

  const getHeight = (
    el: HTMLElement,
    setStateFunc: Dispatch<SetStateAction<number>>
  ) => {
    const height = el.clientHeight
    setStateFunc(height)
  }

  useEffect(() => {
    if(noLogos){
      const interval = setInterval(() => {
        setActiveIndex((prevIndex) => (prevIndex + 1) % items.length);
      }, 10000);

      return () => clearInterval(interval);
  }
  }, [items.length,noLogos,activeIndex]);

  const measureHeight = useCallback(() => {
    const testimonialToMeasure = wrapper?.querySelector(".measure-height")
    if (testimonialToMeasure instanceof HTMLElement) {
      getHeight(testimonialToMeasure, setWrapperHeight)
      if (logoWrap) getHeight(logoWrap, setLogoWrapHeight)
    }
  }, [logoWrap, wrapper])

  const logos = useMemo(() => {
    return items.map((item, index) => {
      return (
        <LogoWrapper
          active={activeIndex === index}
          // eslint-disable-next-line react/no-array-index-key
          key={`${item.title}-${index}`}
          color={primaryColor}
          onClick={() => setActiveIndex(index)}
          onKeyUp={() => setActiveIndex(index)}
          role="button"
          tabIndex={0}
        >
          {item.logo && typeof item.logo === "string" && (
            <Logo src={item.logo} loading="lazy" alt="company logo" />
          )}
          {item.logo && typeof item.logo !== "string" && (
            <LogoImg image={item.logo} loading="lazy" alt="company logo" />
          )}
        </LogoWrapper>
      )
    })
  }, [items, setActiveIndex, activeIndex, primaryColor])

  const dots = useMemo(() => {
    return items.map((item, index) => {
      return (
        <Dots
        active={activeIndex === index}
        // eslint-disable-next-line react/no-array-index-key
        key={`${item.title}-${index}`}
        role="button"
        tabIndex={0}
        onKeyUp={() => setActiveIndex(index)}
        onClick={() => setActiveIndex(index)}
        />
      )
    })
  }, [items, setActiveIndex,activeIndex])

  const testimonials = useMemo(() => {
    const longest = items.reduce((a, b) =>
      a.title.length > b.title.length ? a : b
    )
    if (activePage)
      return items.map((item, index) => {
        const testimonialsArr = activeTestimonialPage?.testimonials

        const testimonialData = testimonialsArr?.find(
          testimonial => testimonial?.testimonialNumber === index + 1
        )
        const shouldMeasure = longest === item || items.length === 1

        return (
          <Content
            className={shouldMeasure ? "measure-height" : ""}
            active={activeIndex === index}
            key={item.person.name}
          >
            <Kicker color={secondaryColor}>{item.kicker}</Kicker>
            <Title color={primaryColor} size={item.quoteSize}>
              {item.title}
            </Title>
            {testimonialData && (
              <Button
                onClick={() =>
                  setActiveVideo(
                    testimonialData.youTubeEmbed?.youtubeVideoId ?? ""
                  )
                }
              >
                {testimonialData.buttonText ?? "Watch Video"}
              </Button>
            )}

            <Person>
              {item.person.image && (
                <Image image={item.person.image} alt={item.person.name} />
              )}
              <Name color={primaryColor}>{item.person.name}</Name>
              <JobTitle color={secondaryColor}>{item.person.title}</JobTitle>
            </Person>
          </Content>
        )
      })
  }, [
    items,
    activeIndex,
    primaryColor,
    secondaryColor,
    activeTestimonialPage,
    setActiveVideo,
    activePage,
  ])

  useEffect(() => {
    if (wrapper) setActivePage(window.location.pathname)
  }, [setActivePage, wrapper])

  useEffect(() => {
    if (wrapper && testimonials && isBrowser()) {
      const testimonialToMeasure = wrapper.querySelector(".measure-height")

      if (testimonialToMeasure instanceof HTMLElement) {
        measureHeight()

        window.addEventListener("resize", measureHeight)
        return () => {
          window.removeEventListener("resize", measureHeight)
          measureHeight()
        }
      }
    }
  }, [wrapper, testimonials, logoWrap, measureHeight])

  return (
    <Wrapper
      wrapperHeight={wrapperHeight}
      logoWrapHeight={logoWrapHeight}
      noLogos={noLogos}
    >
      <TestimonialsWrapper
        wrapperHeight={wrapperHeight}
        ref={ref => setWrapper(ref)}
      >
        {testimonials}
      </TestimonialsWrapper>
      {!noLogos && logos.length > 1 && (
        <Logos ref={ref => setLogoWrap(ref)}>{logos}</Logos>
      )}
      {noLogos &&
        <DotsWrapper>
          {dots}
        </DotsWrapper>
      }
    </Wrapper>
  )
}

const LogoWrapper = styled.div<{ color: string; active: boolean }>`
  border-bottom-style: solid;
  border-width: 1px;
  border-bottom-color: ${props => (props.active ? props.color : "transparent")};
  cursor: pointer;
  transition: 0.4s;
  position: relative;
  div {
    max-width: 6.024vw;
  }
  ${media.fullWidth} {
    padding-bottom: 3px;

    div {
      max-width: 100px;
    }
  }

  ${media.desktop} {
    padding-bottom: 0.18vw;
  }
  ${media.mobile} {
    div {
      max-width: 18vw;
    }
  }
  img {
    filter: brightness(0) invert(1);
    filter: ${({ active }) =>
      !active &&
      `
    brightness(0) saturate(100%) invert(100%) saturate(1456%) hue-rotate(172deg)
      brightness(103%) contrast(108%); opacity: 0.5;`};
  }
`
const Logo = styled.img`
  height: 100%;

  ${media.fullWidth} {
    max-height: 40px;
    width: auto;
  }

  ${media.desktop} {
    max-height: 2.41vw;
    width: auto;
  }

  ${media.tablet} {
    max-height: 3.906vw;
    width: auto;
  }

  ${media.mobile} {
    max-height: 10.667vw;
    width: auto;
  }
`
const LogoImg = styled(GatsbyImage)`
  height: 100%;

  ${media.fullWidth} {
    max-height: 40px;
    width: auto;
  }

  ${media.desktop} {
    max-height: 2.41vw;
    width: auto;
  }

  ${media.tablet} {
    max-height: 3.906vw;
    width: auto;
  }

  ${media.mobile} {
    max-height: 10.667vw;
    width: auto;
  }
`

const Dots = styled.span<{active:boolean}>`
  width: 10px;
  height: 10px;
  background-color: ${props => (props.active ? "white" : "transparent")};
  border: 1px solid;
  border-color: ${colors.blue100};
  border-radius: 50%;
  display: inline-block;
  margin: 0 5px; 
  cursor: pointer;
  position: relative;
`

const Content = styled.div<{ active: boolean }>`
  z-index: ${props => (props.active ? 1 : 0)};
  opacity: ${props => (props.active ? 1 : 0)};
  transition-duration: 1s;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  height: fit-content;

  ${media.fullWidth} {
    gap: 40px;
  }

  ${media.desktop} {
    gap: 2.41vw;
  }

  ${media.tablet} {
    gap: 3.906vw;
  }

  ${media.mobile} {
    gap: 10.667vw;
    padding: 0 5.333vw;
  }
`

const Kicker = styled.span<{ color: string }>`
  text-align: center;
  ${text.captionKicker}
  color: ${props => props.color};

  ${media.mobile} {
    width: 95%;
  }
`

const Title = styled.h2<{ color: string; size?: CSSProp }>`
  ${props => (props.size ? props.size : text.h4)};
  color: ${props => props.color};
  text-align: center;

  ${media.tablet} {
    ${props => (props.size ? props.size : text.h4)};
  }
  ${media.mobile} {
    ${props => (props.size ? props.size : text.h5)};
  }
`

const Person = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Image = styled(GatsbyImage)`
  border-radius: 50%;
  object-fit: cover;
  isolation: isolate;

  ${media.fullWidth} {
    width: 77px;
    height: 77px;
    margin-bottom: 10px;
  }

  ${media.desktop} {
    width: 4.61vw;
    height: 4.61vw;
    margin-bottom: 0.6vw;
  }

  ${media.tablet} {
    width: 5.859vw;
    height: 5.859vw;
    margin-bottom: 0.977vw;
  }

  ${media.mobile} {
    width: 16vw;
    height: 16vw;
    margin-bottom: 2.67vw;
  }
`

const Name = styled.p<{ color: string }>`
  ${text.buttonL}
  color: ${props => props.color};
  text-align: center;
`

const JobTitle = styled.p<{ color: string }>`
  ${text.bodyR}
  text-align: center;
  color: ${props => props.color};
`

const Wrapper = styled.div<{
  wrapperHeight: number
  logoWrapHeight: number
  noLogos: boolean
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  ${media.fullWidth} {
    padding: 150px 0;
    height: calc(
      ${props => props.wrapperHeight}px +
        ${({ noLogos }) => (noLogos ? "0" : "410px")}
    );
  }

  ${media.desktop} {
    height: calc(
      ${props => props.wrapperHeight}px +
        ${({ noLogos }) => (noLogos ? "0" : "24.699vw")}
    );
    padding: 9.036vw 0;
  }

  ${media.tablet} {
    gap: 3.906vw;
    height: calc(
      ${props => props.wrapperHeight}px +
        ${({ noLogos }) => (noLogos ? "0" : "40.039vw")}
    );
    padding: 14.648vw 0;
  }

  ${media.mobile} {
    gap: 5.333vw;
    height: calc(
      ${props => props.wrapperHeight}px +
        ${({ logoWrapHeight }) => logoWrapHeight}px +
        ${({ noLogos }) => (noLogos ? "0" : "69.333vw")}
    );
    padding: 25vw 0;
  }
`

const TestimonialsWrapper = styled.div<{ wrapperHeight: number }>`
  transition-duration: 1s;
  display: flex;
  flex-direction: column;
  height: ${props => props.wrapperHeight}px;
  width: 100%;
  position: relative;

  ${media.fullWidth} {
    gap: 40px;
  }

  ${media.desktop} {
    gap: 2.41vw;
  }

  ${media.tablet} {
    gap: 3.906vw;
  }

  ${media.mobile} {
    gap: 5.333vw;
  }
`
const Logos = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  height: 60px;
  width: 50%;
  left: 25%;

  ${media.fullWidth} {
    gap: 40px;
    bottom: 150px;
  }

  ${media.desktop} {
    gap: 2.41vw;
    bottom: 9.036vw;
  }

  ${media.tablet} {
    gap: 3.906vw;
    bottom: 14.65vw;
  }

  ${media.mobile} {
    flex-wrap: wrap;
    height: fit-content;
    row-gap: 10.667vw;
    column-gap: 8vw;
    bottom: 25vw;
  }
`

const DotsWrapper = styled.div`
  margin-right: auto;
  margin-left: auto;
`
